import React from 'react';

export const PropertyTypesWeBuy: React.FC = () => {
  const propertyTypes = [
    {
      title: 'Small Garden Communities',
      units: '20-50 Units',
      description: 'Intimate garden apartment properties perfect for owner-operators or small investors. We understand the unique challenges of smaller properties and provide fair valuations.',
      features: ['Single or multiple buildings', 'Limited amenities', 'Owner-managed or small staff', 'Neighborhood integration']
    },
    {
      title: 'Mid-Size Garden Complexes',
      units: '51-150 Units',
      description: 'The sweet spot for garden apartment investments. These properties offer economies of scale while maintaining the garden apartment character and appeal.',
      features: ['Multiple buildings', 'Basic amenities (pool, clubhouse)', 'Professional management', 'Established operations']
    },
    {
      title: 'Large Garden Communities',
      units: '151-300 Units',
      description: 'Substantial garden apartment communities with comprehensive amenities and professional management structures. We have the capital and experience for larger acquisitions.',
      features: ['Extensive amenities', 'Multiple property managers', 'Complex operations', 'Significant land holdings']
    },
    {
      title: 'Portfolio Opportunities',
      units: '300+ Units',
      description: 'Multiple garden apartment properties or very large communities. We can structure portfolio purchases and handle complex multi-property transactions.',
      features: ['Multiple locations', 'Institutional quality', 'Sophisticated operations', 'Bulk purchase advantages']
    }
  ];

  const conditions = [
    {
      icon: 'ri-star-line',
      title: 'Excellent Condition',
      description: 'Recently renovated properties with updated units, modern amenities, and well-maintained grounds.'
    },
    {
      icon: 'ri-tools-fill',
      title: 'Needs Renovation',
      description: 'Properties requiring unit updates, building improvements, or amenity upgrades to compete effectively.'
    },
    {
      icon: 'ri-alert-line',
      title: 'Significant Deferred Maintenance',
      description: 'Properties with substantial repair needs including roofs, parking lots, building systems, or landscaping.'
    },
    {
      icon: 'ri-building-line',
      title: 'Functional Obsolescence',
      description: 'Older properties with outdated layouts, small units, or amenities that no longer meet market expectations.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Garden Apartment Properties We Purchase
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            We buy garden apartments of all sizes and conditions. From small communities to large portfolios, we have the expertise and capital to close your transaction.
          </p>
        </div>

        <div className="mb-20">
          <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            Property Sizes We Purchase
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {propertyTypes.map((type, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-orange-50 to-white rounded-xl border border-orange-100 p-8 hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-6">
                  <div className="inline-block px-4 py-2 bg-orange-500 text-white font-bold rounded-lg mb-4">
                    {type.units}
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">
                    {type.title}
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {type.description}
                  </p>
                </div>
                <div className="space-y-3">
                  {type.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/445d682c8464a1047fffac74038896c0.jpg"
              alt="Variety of Garden Apartment Properties"
              className="w-full h-[450px] object-cover object-top"
            />
          </div>
        </div>

        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            Property Conditions We Accept
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conditions.map((condition, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-orange-500 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${condition.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">
                  {condition.title}
                </h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {condition.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-10 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">
            Not Sure If We'll Buy Your Property?
          </h3>
          <p className="text-xl mb-8 text-white/95">
            Contact us today. We evaluate every garden apartment opportunity and provide honest feedback about whether we can make an offer.
          </p>
          <a
            href="#get-offer"
            className="inline-block px-10 py-4 bg-white text-orange-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Get Your Free Property Evaluation
          </a>
        </div>
      </div>
    </section>
  );
};